import { ErrorHandler, Injectable } from '@angular/core';
import { isPermissionDeniedError } from '@core/services/auth/auth-transition-error';
import { isChunkLoadError, tryRecoverFromChunkError } from '@core/services/infra/chunk-load-recovery';
import { captureAppException } from '@core/observability/sentry';
import { recordError } from '@core/observability/error-trail';

/**
 * Patch `console.error` once, at module load, to silently swallow Firestore
 * permission-denied errors from anywhere in the app (including code paths
 * that bypass Angular's ErrorHandler — e.g. `console.error('foo:', error)`
 * inside an `onSnapshot` onError callback or a service's manual catch block).
 *
 * This is a runtime-level filter, not a code-level fix. It complements (not
 * replaces) the per-service `catchError` silencing — defense in depth.
 *
 * Idempotent: re-imports of this module won't double-patch.
 */
const PATCH_FLAG = '__permissionFilterPatched';
type PatchedConsole = typeof console & { [PATCH_FLAG]?: boolean };

(function patchConsoleErrorOnce() {
  const c = console as PatchedConsole;
  if (c[PATCH_FLAG]) return;
  const original = c.error.bind(c);
  c.error = (...args: unknown[]) => {
    if (args.some(arg => isPermissionDeniedError(arg))) {
      return;
    }
    original(...args);
  };
  c[PATCH_FLAG] = true;
})();

/**
 * Global error handler that silently swallows Firestore "permission-denied"
 * errors anywhere in the app.
 *
 * Why this is here as a *global* handler (in addition to the per-service
 * `handleError` silencing): there are many surfaces from which a Firestore
 * listener can throw — `effect(async () => ...)`, `toSignal(observable)`,
 * `computed()` consumers of an observable that errors, or any service whose
 * own `catchError` we haven't audited yet. Plugging the leak at the root
 * `ErrorHandler` is the only durable fix that doesn't require chasing every
 * call-site, and matches what the per-service silencing intends:
 *
 *   - Logout/login transitions: in-flight listeners on the previous account
 *     fire one final permission-denied before unsubscribe propagates.
 *   - Workspace claim missing on fresh signup: recovers when the
 *     `createPersonalWorkspace` Cloud Function refreshes the ID token.
 *   - Firestore rules bug in development: would also surface in WRITE
 *     operations (addDoc/updateDoc/deleteDoc), which throw separately and
 *     are handled by their own catchError paths — silencing reads here
 *     does NOT mask write-side bugs.
 *
 * Anything else falls through to the default error handler (console.error +
 * Angular's normal reporting) AND is reported to Sentry. Reporting deliberately
 * sits after every filter above: permission-denied and chunk-load errors are
 * expected, self-healing noise, and paying Sentry quota for them would bury the
 * real crashes. See `core/observability/sentry.ts`.
 */
@Injectable()
export class PermissionErrorFilteringHandler extends ErrorHandler {
  override handleError(error: unknown): void {
    if (isPermissionDeniedError(error)) {
      return;
    }
    // A lazy route failed to load its chunk — almost always because the tab is
    // running an `index.html` from before the last deploy (stale hashes). Force
    // one full reload to pull the fresh build. See chunk-load-recovery.ts.
    if (isChunkLoadError(error) && this.recoverFromChunkError()) {
      return;
    }
    // Unwrap rejection from async effects: `Promise.reject(firebaseErr)`
    // arrives here as `{ rejection: firebaseErr }` in some Angular versions.
    if (typeof error === 'object' && error !== null && 'rejection' in error) {
      const rejection = (error as { rejection: unknown }).rejection;
      if (isPermissionDeniedError(rejection)) {
        return;
      }
    }
    // Same position as the Sentry report, and for the same reason: everything
    // filtered above is expected, self-healing noise. A user attaching their
    // error trail to a support ticket (#284) should see what actually broke,
    // not ten permission-denied lines from a workspace switch.
    recordError(error);
    this.report(error);
    super.handleError(error);
  }

  /**
   * Forward a surviving error to Sentry. Extracted as a protected method so
   * tests can assert that filtered errors never reach it.
   */
  protected report(error: unknown): void {
    captureAppException(error);
  }

  /**
   * Trigger the one-shot reload recovery. Extracted as a protected method so
   * tests can stub it (the underlying free function reads the real `window`).
   */
  protected recoverFromChunkError(): boolean {
    return tryRecoverFromChunkError();
  }
}
